import { toast } from 'sonner';
import i18next from 'i18next';
import { useScopeDocumentStore } from '../../store/scope-document.store.js';
import { useTenantStore } from '../../store/tenant.store.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';
export class ScopeDocumentIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    async fetchAll(page = 1, pageSize = 10, search = '', sortBy = '', sortOrder = 'desc', columnFilters = {}) {
        const store = useScopeDocumentStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;
        store.setLoading(true);
        store.clearError();
        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }
        try {
            const response = await this.service.getAll({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                tenantId,
                populate: 'linkedDocumentId',
                ...Object.entries(columnFilters).reduce((acc, [key, value]) => {
                    if (value && value.length > 0) acc[`filter_${key}`] = value;
                    return acc;
                }, {})
            });
            const { data, pagination, uniqueValues } = response;
            store.setScopeDocuments(data || []);
            if (pagination) {
                store.setPaginationData({
                    currentPage: pagination.page,
                    totalPages: pagination.totalPages,
                    totalItems: pagination.total,
                    itemsPerPage: pagination.pageSize,
                });
            }
            if (uniqueValues) store.setUniqueValues(uniqueValues);
            return { success: true };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error) || i18next.t('integrationToasts:couldNotLoadList', { defaultValue: 'Could not load list' });
            store.setError(msg);
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }

    async create(data) {
        const store = useScopeDocumentStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.create(data);
            toast.success(i18next.t('contextManagementScopeDocument:messages.createSuccess', { defaultValue: 'Record created' }));
            return { success: true, data: response.data };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error) || i18next.t('integrationToasts:couldNotCreateRecord', { defaultValue: 'Could not create record' });
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }

    async update(id, data) {
        const store = useScopeDocumentStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.update(id, data);
            toast.success(i18next.t('contextManagementScopeDocument:messages.updateSuccess', { defaultValue: 'Record updated' }));
            return { success: true, data: response.data };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error) || i18next.t('integrationToasts:couldNotUpdateRecord', { defaultValue: 'Could not update record' });
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }

    async delete(id) {
        const store = useScopeDocumentStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.delete(id);
            toast.success(i18next.t('contextManagementScopeDocument:messages.deleteSuccess', { defaultValue: 'Record deleted' }));
            return { success: true, data: response.data };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error) || i18next.t('integrationToasts:couldNotDeleteRecord', { defaultValue: 'Could not delete record' });
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }
}
